import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import { 
  getVisiblePaths, 
  useMapPointSelect,
  useModeSwitching,
  usePathManagement
} from '../Services';

// Create context
const TspContext = createContext();

// Provide a hook to use the context
export const useTsp = () => {
  const context = useContext(TspContext);
  if (!context) {
    throw new Error('useTsp must be used within a TspProvider');
  }
  return context;
};

// Context provider component
export const TspProvider = ({ children }) => {
  // Base state
  const [locations, setLocations] = useState([]);
  const [startId, setStartId] = useState('');
  const [visualizationPath, setVisualizationPath] = useState(null);
  const [showPanel, setShowPanel] = useState(true);

  // Use existing service hooks
  const { 
    currentMode, 
    setCurrentMode,
    currentModeRef,
    setupSubmode, 
    handleSubmodeSwitching
  } = useModeSwitching();

  // Use path management hook with enhanced return values
  const {
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath,
    saveAutoPath,
    saveCustomPath
  } = usePathManagement(locations);

  // Use map point selection hook with enhanced return values
  const { 
    mapPointSelectCallback, 
    setMapPointSelect,
    clearMapPointSelect 
  } = useMapPointSelect();

  // Map reference for jump-to-location functionality
  const mapRef = useRef(null);

  // Function to jump to a specific location on the map
  const jumpToLocation = useCallback((latitude, longitude, zoom = 15) => {
    if (!mapRef.current || !mapRef.current.loaded()) {
      console.warn('Map is not ready for jumping to location');
      return;
    }

    try {
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: zoom,
        duration: 1000, // Animation duration in milliseconds
        essential: true // This animation is considered essential with respect to prefers-reduced-motion
      });
    } catch (error) {
      console.error('Error jumping to location:', error);
    }
  }, []);

  // Function to set map reference (to be called from MapComponent)
  const setMapRef = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Callback to update visualization path
  const handleUpdateVisiblePath = useCallback((partialPath) => {
    if (partialPath === null) {
      setVisualizationPath(null);
    } else if (Array.isArray(partialPath) && partialPath.length > 0) {
      setVisualizationPath(partialPath);
    }
  }, []);

  // Callback to clear all paths
  const clearAllPaths = useCallback(() => {
    setAutoPath([]);
    setCustomPath([]);
    setVisualizationPath(null);
  }, [setAutoPath, setCustomPath]);

  // Calculate visible paths for current mode
  const visiblePaths = useMemo(() => {
    // If there's a visualization path and in Auto Path mode, use it
    if (currentMode === 'Auto Path' && visualizationPath !== null) {
      return { auto: visualizationPath };
    }
    
    // Otherwise use normal path logic
    return getVisiblePaths(currentMode, autoPath, customPath);
  }, [currentMode, autoPath, customPath, visualizationPath]);

  // Value provided to context
  const contextValue = {
    // Base state
    locations,
    setLocations,
    startId,
    setStartId,
    showPanel,
    setShowPanel,
    
    // Mode management
    currentMode,
    setCurrentMode,
    currentModeRef,
    setupSubmode,
    handleSubmodeSwitching,
    
    // Path management
    autoPath,
    setAutoPath,
    customPath,
    setCustomPath,
    prevAutoPathRef,
    prevCustomPathRef,
    setPath: (path) => setPath(path, currentMode),
    saveAutoPath,
    saveCustomPath,
    clearAllPaths,
    visualizationPath,
    handleUpdateVisiblePath,
    visiblePaths,
    
    // Map interaction
    mapPointSelectCallback,
    setMapPointSelect,
    clearMapPointSelect,
    jumpToLocation,
    setMapRef
  };

  return (
    <TspContext.Provider value={contextValue}>
      {children}
    </TspContext.Provider>
  );
}; 